import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["checkbox", "selectedItems"];
  
  static values = {
    filterParam: String
  };
  
  static outlets = ["filters"];

  connect() {
    // Initialize controller
    this.filterParam = this.element.dataset.multiFilterFilterParam || '';
    
    // Load values from URL params
    this.loadFromUrlParams();
  }
  
  /**
   * Load selected values from URL parameters
   * This ensures the checkboxes reflect any filter values in the URL
   */
  loadFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    
    // Check for comma-separated values first
    const commaSeparatedValue = params.get(this.filterParam);
    
    // Parse values from either comma-separated string or array notation
    let values = [];
    if (commaSeparatedValue) {
      values = commaSeparatedValue.split(',').map(v => v.trim());
    } else {
      // Fallback to array notation for backward compatibility
      values = params.getAll(`${this.filterParam}[]`);
    }
    
    if (!values || values.length === 0) return;
    
    // Ensure values are unique
    const uniqueValues = Array.from(new Set(values));
    
    // Check corresponding checkboxes and update selected items
    uniqueValues.forEach(value => {
      const checkbox = this.checkboxTargets.find(cb => cb.value === value);
      
      if (checkbox) {
        checkbox.checked = true;
        this.addSelectedItem(value, checkbox.dataset.label || value, true);
      }
    });
  }
  
  /**
   * Handle checkbox toggle
   * @param {Event} event - The change event
   */
  toggleCheckbox(event) {
    // Prevent event from bubbling up to dropdown or document
    event.stopPropagation();
    
    const checkbox = event.currentTarget;
    const value = checkbox.value;
    const label = checkbox.dataset.label || value;
    
    if (checkbox.checked) {
      this.addSelectedItem(value, label);
    } else {
      this.removeSelectedItem(value);
    }
  }
  
  /**
   * Add a selected item to the display
   * @param {string} value - The value to add
   * @param {string} label - The display label
   * @param {boolean} initializing - Whether this is part of initialization
   */
  addSelectedItem(value, label, initializing = false) {
    // Check if item already exists
    if (this.selectedItemsTarget.querySelector(`[data-value="${value}"]`)) {
      return;
    }
    
    // Create visual chip for selected item
    const chip = document.createElement('div');
    // Use the selected-item-chip class for consistency with filters controller
    chip.classList.add('selected-item-chip', 'inline-flex', 'items-center', 'px-2', 'py-1', 'rounded-full', 'text-xs', 'font-medium', 'bg-indigo-100', 'text-indigo-800', 'mr-2', 'mb-1');
    chip.dataset.value = value;
    chip.dataset.filterParam = this.filterParam; // Add filterParam for active filters detection
    
    // Add label text
    const labelSpan = document.createElement('span');
    labelSpan.textContent = label;
    chip.appendChild(labelSpan);
    
    // Add remove button
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.classList.add('ml-1', 'text-indigo-500', 'hover:text-indigo-700', 'focus:outline-none', 'cursor-pointer');
    removeButton.innerHTML = '&times;';
    removeButton.dataset.action = 'multi-filter#removeChip';
    removeButton.dataset.value = value;
    chip.appendChild(removeButton);
    
    // Add to container
    this.selectedItemsTarget.appendChild(chip);
    
    // Update filter
    this.updateFilter(initializing);
  }
  
  /**
   * Remove a selected item
   * @param {string} value - The value to remove
   */
  removeSelectedItem(value) {
    // Remove chip
    const chip = this.selectedItemsTarget.querySelector(`[data-value="${value}"]`);
    if (chip) {
      chip.remove();
    }
    
    // Uncheck corresponding checkbox
    const checkbox = this.checkboxTargets.find(cb => cb.value === value);
    if (checkbox) {
      checkbox.checked = false;
    }
    
    // Update filter
    this.updateFilter();
  }
  
  /**
   * Remove a chip when the remove button is clicked
   * @param {Event} event - The click event
   */
  removeChip(event) {
    // Prevent event from bubbling up to document
    // This prevents the dropdown from closing when clicking the X
    event.stopPropagation();
    
    const value = event.currentTarget.dataset.value;
    this.removeSelectedItem(value);
  }
  
  /**
   * Update filter with current selected values
   * Sends the current selected values to the filters controller
   * @param {boolean} initializing - Whether this update is part of initialization
   */
  updateFilter(initializing = false) {
    // Get all selected values and ensure they're unique
    const values = Array.from(new Set(
      Array.from(this.selectedItemsTarget.querySelectorAll('[data-value]'))
        .map(chip => chip.dataset.value)
    ));
    
    // Send to filters controller
    this.filtersOutlet.updateMultiFilter(this.filterParam, values, initializing);
  }
  
  /**
   * Clear all selected items
   */
  clear() {
    // Clear all chips
    this.selectedItemsTarget.innerHTML = '';
    
    // Uncheck all checkboxes
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Update filter
    this.updateFilter();
  }
}
